import React, { useEffect, useState } from "react";
import "./BusinessListing.scss";
import { toast, Toaster } from "sonner";

// PrimeReact
import { Calendar } from "primereact/calendar";
import { Button as PrimeButton } from "primereact/button";

// Components
import CardView from "../../components/molecules/CardView/CardView";
import Input from "../../components/atoms/Input/Input";
import Button from "../../components/atoms/Button/Button";
import SmoothContainer from "../../components/atoms/SmoothContainer/SmoothContainer";
import DataTable from "../../components/organisms/DataTable/DataTable";

// Services
import { getBusiness, updateBussiness } from "../../services/businnes";
import { impersonateUser } from "../../services/Auth";

// Assets
import Report from "../../assets/icons/Report.svg";

// Router
import { useNavigate } from "react-router-dom";

const BusinessListing = () => {
    const navigate = useNavigate();
    const widthScreen = window.innerWidth;

    // Estados principales
    const [business, setBusiness] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState("");
    const [businessSearch, setBusinessSearch] = useState("");

    // Estados UI
    const [openReport, setOpenReport] = useState(false);
    const [height, setHeight] = useState(107);

    // Estados para fechas del reporte
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    useEffect(() => {
        if (widthScreen <= 767) {
            setHeight(219);
        } else if (widthScreen <= 991) {
            setHeight(239);
        }
    }, [widthScreen]);

    // Obtener negocios con filtros
    const fetchBusinesses = async (search = "", status = "") => {
        try {
            setLoading(true);
            const payload = {
                searchResult: search,
                statusResult: status,
            };

            const response = await getBusiness(payload);

            const { data } = response.data;

            setBusiness(data);
        } catch (error) {
            console.error("Error al obtener negocios:", error);
        } finally {
            setLoading(false);
        }
    };

    // Manejar búsqueda
    const handleSearch = async (e) => {
        e.preventDefault();
        const trimmedSearch = businessSearch.trim();

        if (!trimmedSearch) {
            return;
        }

        await fetchBusinesses(trimmedSearch, statusFilter);
    };

    // Manejar cambio de estado
    const handleStatusChange = async (type) => {
        setStatusFilter(type);
        await fetchBusinesses(businessSearch || "", type);
    };

    useEffect(() => {
        let timer;
        if (openReport) {
            timer = setTimeout(() => {
                setOpenReport(false);
            }, 60000);
        }
        // Limpiar el temporizador si el componente se desmonta o si `openReport` cambia
        return () => clearTimeout(timer);
    }, [openReport]);

    // Manejar generación de reporte
    const handleGenerateReport = () => {
        if (!startDate || !endDate) {
            console.warn("Seleccione ambas fechas para generar el reporte");
            return;
        }

        // Aquí iría la lógica para generar el reporte
    };

    // Componente del reporte
    const InterfaceReport = () => {
        return (
            <div className="business-listing__report">
                <div className="business-listing__report-inputs">
                    <div className="business-listing__date-group">
                        <span className="business-listing__date-label">
                            Desde
                        </span>
                        <Calendar
                            value={startDate}
                            onChange={(e) => setStartDate(e.value)}
                            dateFormat="dd/mm/yy"
                            placeholder="Seleccionar fecha"
                            showIcon
                            className="business-listing__calendar"
                            maxDate={endDate || new Date()}
                        />
                    </div>
                    <div className="business-listing__date-group">
                        <span className="business-listing__date-label">
                            Hasta
                        </span>
                        <Calendar
                            value={endDate}
                            onChange={(e) => setEndDate(e.value)}
                            dateFormat="dd/mm/yy"
                            placeholder="Seleccionar fecha"
                            showIcon
                            className="business-listing__calendar"
                            minDate={startDate}
                            maxDate={new Date()}
                        />
                    </div>
                </div>
                <Button
                    text={"Generar"}
                    className="degrad business-listing__report-btn"
                    onClick={handleGenerateReport}
                />
            </div>
        );
    };

    // Badge de estado
    const StatusBadge = ({ status }) => {
        const isActive = status === "activo";
        return (
            <span
                className={`business-listing__status-badge business-listing__status-badge--${status}`}
            >
                {isActive ? "Activo" : "Inactivo"}
            </span>
        );
    };

    const remainingDays = (billingEndDate) => {
        if (billingEndDate === "N/A") return "N/A";

        const [day, month, year] = billingEndDate.split("/").map(Number);

        // Validar que los valores sean números válidos
        if (!day || !month || !year) return "N/A";

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const billingDate = new Date(year, month - 1, day);

        // Verificar que la fecha sea válida
        if (isNaN(billingDate.getTime())) return "N/A";

        const diffTime = billingDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        const total = Math.max(diffDays, 0);

        const getColor = () => {
            if (total <= 1) return { bg: "#ffb3ba", text: "#8b0000" }; // Rojo pastel
            if (total <= 5) return { bg: "#ffd4a3", text: "#8b4513" }; // Naranja pastel
            if (total <= 10) return { bg: "#fff4a3", text: "#856404" }; // Amarillo pastel
            return { bg: "#bae1c8", text: "#155724" }; // Verde pastel
        };

        const colors = getColor();

        return (
            <span
                className="business-payments__badge"
                style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                }}
            >
                {total} {total === 1 ? "día" : "días"}
            </span>
        );
    };

    // Columnas de la tabla
    const columns = [
        {
            field: "businessName",
            header: "Nombre del negocio",
        },
        {
            field: "owner",
            header: "Nombre del propietario",
        },
        {
            field: "createdAt",
            header: "Fecha de creación",
        },
        {
            field: "membership",
            header: "Plan",
        },

        {
            field: "days",
            header: "Días restantes",
            body: (rowData) => remainingDays(rowData.billingEndDate),
        },
        {
            field: "billingEndDate",
            header: "Fin de facturación",
        },
        {
            field: "status",
            header: "Estado",
            body: (rowData) => <StatusBadge status={rowData.status} />,
        },
        {
            field: "actions",
            header: "Acciones",
            body: (rowData) => {
                const isInactive = rowData.status === "inactivo";

                return (
                    <div className="business-listing__actions">
                        <PrimeButton
                            icon="pi pi-pencil"
                            rounded
                            text
                            severity="info"
                            className="business-listing__action-btn business-listing__action-btn--edit"
                            onClick={() => handleEdit(rowData)}
                            tooltip={
                                isInactive
                                    ? `No se puede editar ${rowData.businessName} inactivo`
                                    : `Editar ${rowData.businessName}`
                            }
                            tooltipOptions={{ position: "top" }}
                            disabled={isInactive}
                        />
                        <PrimeButton
                            icon={
                                rowData.status === "activo"
                                    ? "pi pi-times-circle"
                                    : "pi pi-check-circle"
                            }
                            rounded
                            text
                            severity={
                                rowData.status === "activo"
                                    ? "danger"
                                    : "success"
                            }
                            className={`business-listing__action-btn business-listing__action-btn--${
                                rowData.status === "activo"
                                    ? "deactivate"
                                    : "activate"
                            }`}
                            onClick={() => handleToggleStatus(rowData)}
                            tooltip={
                                rowData.status === "activo"
                                    ? `Desactivar ${rowData.businessName}`
                                    : `Activar ${rowData.businessName}`
                            }
                            tooltipOptions={{ position: "top" }}
                        />

                        <PrimeButton
                            icon="pi pi-link"
                            tooltip={
                                isInactive
                                    ? "No se puede conectar a un negocio inactivo"
                                    : `Conectar como ${rowData.businessName}`
                            }
                            tooltipOptions={{ position: "left" }}
                            className="business-listing__action-btn business-listing__action-btn--conect"
                            onClick={() => handleImpersonate(rowData)}
                            disabled={loading || isInactive}
                        />
                        {/* <PrimeButton
                            icon="pi pi-eye"
                            rounded
                            text
                            severity="help"
                            className="business-listing__action-btn business-listing__action-btn--view"
                            onClick={() => handleView(rowData)}
                            tooltip="Ver detalles"
                            tooltipOptions={{ position: "top" }}
                    /> */}
                    </div>
                );
            },
        },
    ];

    // Acciones de la tabla
    const handleEdit = (rowData) => {
        if (rowData.status === "inactivo") {
            toast.error("Operación no permitida", {
                description:
                    "No se puede editar un negocio inactivo. Actívalo primero.",
                position: "top-right",
                duration: 4000,
            });
            return;
        }
        navigate(`/editar-negocio/editar/${rowData.id}`);
    };

    const handleToggleStatus = async (rowData) => {
        try {
            const newStatus =
                rowData.status === "activo" ? "inactivo" : "activo";
            const response = await updateBussiness(rowData.id, {
                status: newStatus,
            });

            if (response.success) {
                setBusiness((prev) =>
                    prev.map((b) =>
                        b.id === rowData.id ? { ...b, status: newStatus } : b
                    )
                );
            }
        } catch (error) {
            console.error("Error al cambiar estado:", error);
        }
    };

    // Manejar conexión como usuario (impersonate)
    const handleImpersonate = async (rowData) => {
        if (rowData.status === "inactivo") {
            toast.error("Operación no permitida", {
                description:
                    "No se puede conectar a un negocio inactivo. Actívalo primero.",
                position: "top-right",
                duration: 4000,
            });
            return;
        }

        try {
            setLoading(true);

            // Llamar al endpoint de impersonate
            const response = await impersonateUser(rowData.userId);

            if (response && response.exchangeToken) {
                // En lugar de usar localStorage (que no se comparte entre puertos),
                // vamos a pasar los datos codificados en la URL
                const target = response.exchangeToken;

                // URL de la aplicación del usuario
                const userAppUrl =
                    process.env.REACT_APP_USER_APP_URL ||
                    "http://localhost:5173";

                const impersonateUrl = `${userAppUrl}/impersonate?target=${target}`;

                // Redirigir a la aplicación del usuario pasando los datos
                window.open(impersonateUrl, "_blank");
            } else {
                alert("Error al obtener los tokens de autenticación");
            }
        } catch (error) {
            console.error("Error al conectar como usuario:", error);
            alert(
                "Error al conectar como usuario. Por favor, intente nuevamente."
            );
        } finally {
            setLoading(false);
        }
    };

    // Cargar negocios al inicio
    useEffect(() => {
        fetchBusinesses();
    }, []);

    return (
        <CardView>
            <div className="business-listing">
                <h1 className="business-listing__title">Listado de Negocios</h1>

                <div className="business-listing__header">
                    <div className="business-listing__filters">
                        <Button
                            text={"Todos"}
                            className={`business-listing__filter-btn degrad ${
                                statusFilter === ""
                                    ? "business-listing__filter-btn--all"
                                    : ""
                            }`}
                            onClick={() => handleStatusChange("")}
                        />
                        <Button
                            text={"Activos"}
                            className={`business-listing__filter-btn degrad ${
                                statusFilter === "activo"
                                    ? "business-listing__filter-btn--active"
                                    : ""
                            }`}
                            onClick={() => handleStatusChange("activo")}
                        />
                        <Button
                            text={"Inactivos"}
                            className={`business-listing__filter-btn degrad ${
                                statusFilter === "inactivo"
                                    ? "business-listing__filter-btn--inactive"
                                    : ""
                            }`}
                            onClick={() => handleStatusChange("inactivo")}
                        />
                    </div>

                    <form
                        onSubmit={handleSearch}
                        className="business-listing__search-form"
                    >
                        <Input
                            placeholder={"Buscar negocio..."}
                            type="search"
                            className="business-listing__search-input"
                            onChange={(e) => setBusinessSearch(e.target.value)}
                        />
                        <button
                            type="submit"
                            className="business-listing__search-btn"
                        >
                            Buscar
                        </button>
                    </form>

                    <Button
                        icon={Report}
                        text={"Generar reporte"}
                        className="degrad business-listing__report-toggle"
                        onClick={() => setOpenReport(!openReport)}
                    />
                </div>

                <SmoothContainer actionState={openReport} height={height}>
                    <InterfaceReport />
                </SmoothContainer>

                <DataTable
                    data={business}
                    columns={columns}
                    loading={loading}
                    // showActions={true}
                    // onEdit={handleEdit}
                    // onDelete={handleDelete}
                    emptyMessage="No se encontraron negocios"
                    striped={true}
                    hoverable={true}
                    responsiveLayout="scroll"
                />
            </div>
            <Toaster />
        </CardView>
    );
};

export default BusinessListing;
